import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnnouncementSubscriptionDialogComponent } from '../announcement-subscription-dialog/announcement-subscription-dialog.component';
import { NewAnnouncementDialogComponent } from '../new-announcement-dialog/new-announcement-dialog.component';
import { Subject } from 'rxjs';
import { AnnouncementsService } from '../services/announcements.service';
import { Announcement } from '../models/announcement';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { AnnouncementSubscription } from '../models/AnnouncementSubscription';
import { ToastService } from '../services/toast.service';
import { UserProfileService } from '../services/user-profile.service';
import { LoadingService } from '../services/loading.service';

@Component({
  selector: 'app-announcements',
  templateUrl: './announcements.component.html',
  styleUrls: ['./announcements.component.css'],
  standalone: false,
})
export class AnnouncementsComponent implements OnInit, OnDestroy {
  @Input() hideHeader: boolean = false;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  announcements: Announcement[] = [];
  subscriptions: AnnouncementSubscription[] = [];
  page = -1;
  pageSize = 10;
  isSubscribeModalOpen = false;
  subscriptionEmail = '';
  announcementTypes: string[] = [
    'Critical',
    'Information',
    'Release Notes',
    'Warning',
    'Maintenance',
  ];
  selectedTypes: string[] = [];
  isPlatform: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private readonly userProfileService: UserProfileService,
    private readonly announcementService: AnnouncementsService,
    private readonly toast: ToastService,
    public readonly loadingService: LoadingService
  ) {}

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  ngOnInit(): void {
    // Get user information from claims
    const claims = this.userProfileService.getActiveClaims();
    if (claims) {
      // Check if user is a platform team member
      this.userProfileService
        .isPlatformTeamMember()
        .subscribe((isPlatformMember) => {
          this.isPlatform = isPlatformMember;
        });
    }

    this.loadAnnouncements();
    this.loadSubscriptions();
  }

  ngAfterViewInit(): void {
    this.scrollContainer.nativeElement.addEventListener(
      'scroll',
      this.onScroll.bind(this)
    );
  }

  getAnnouncementClass(type: string): string {
    switch (type) {
      case 'Critical':
        return 'announcement-critical';
      case 'Information':
        return 'announcement-information';
      case 'Release Notes':
        return 'announcement-release-notes';
      case 'Warning':
        return 'announcement-warning';
      default:
        return 'announcement-default';
    }
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.code === 'Enter' || event.code === 'Space') {
      this.openSubscribeModal();
    }
  }

  openSubscribeModal() {
    const dialogRef = this.dialog.open(
      AnnouncementSubscriptionDialogComponent,
      {
        data: {
          announcementTypes: this.announcementTypes,
          subscriptions: this.subscriptions,
          announcementService: this.announcementService,
        },
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
      if (result) {
        this.announcementService
          .subscribeToAnnouncements(result)
          .subscribe((response) => {
            if (response.status === 200) {
              this.loadSubscriptions();
              this.toast.success(
                `You have successfully subscribed ${result.SubscriberEmail} to announcements`
              );
            } else {
              this.toast.error(
                'An error occurred while subscribing to announcements'
              );
            }
          });
      } else {
        this.loadSubscriptions();
      }
    });
  }

  openCreateAnnouncementModal(
    announcement?: Announcement | undefined | null
  ): void {
    const dialogRef = this.dialog.open(NewAnnouncementDialogComponent, {
      data: {
        announcementTypes: this.announcementTypes,
        announcement: announcement,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.announcementService
          .createOrUpdateAnnouncement(result)
          .subscribe(() => {
            if (announcement) {
              const index = this.announcements.indexOf(announcement);
              if (index !== -1) {
                this.announcements.splice(index, 1); // Remove the existing announcement
                this.announcements.splice(index, 0, result); // Insert the updated announcement at the same index
              }
            } else {
              this.announcements.unshift(result); // Add new announcement to the top of the list
            }
          });
      }
    });
  }

  loadAnnouncements(): void {
    this.loadingService.show('announcements');
    this.page += 1;
    this.announcementService
      .getAnnouncements(this.page, this.pageSize)
      .subscribe({
        next: (announcementResponse) => {
          this.announcements.push(
            ...announcementResponse.announcements.map((announcement) => ({
              ...announcement,
              announcementMessage: announcement.announcementMessage,
            }))
          );
          this.loadingService.hide('announcements');
        },
        error: (error) => {
          console.error('Error loading announcements:', error);
          this.loadingService.hide('announcements');
        },
      });
  }

  onScroll(event: any) {
    const element = event.target;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight) {
      this.loadAnnouncements();
    }
  }

  updateAnnouncement(announcement: Announcement): void {
    this.openCreateAnnouncementModal(announcement);
    this.announcementService
      .createOrUpdateAnnouncement(announcement)
      .subscribe(() => {
        this.loadAnnouncements();
      });
  }

  deleteAnnouncement(announcement: Announcement): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        confirmationText: 'Are you sure you want to delete this announcement?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.announcementService
          .deleteAnnouncement(announcement)
          .subscribe(() => {
            this.announcements = this.announcements.filter(
              (x) => x.announcementId !== announcement.announcementId
            );
          });
      }
    });
  }

  loadSubscriptions(): void {
    this.loadingService.show('announcements');
    this.page += 1;
    this.announcementService.getSubscriptions().subscribe({
      next: (subscriptions) => {
        this.subscriptions = subscriptions;
        this.loadingService.hide('announcements');
      },
      error: (error) => {
        console.error('Error loading subscriptions:', error);
        this.loadingService.hide('announcements');
      },
    });
  }
}
